import { useState, useEffect } from 'react'
import apiClient from '../api/client'

// Module-level cache — shared across all client components, only one API call needed
let _cached: string | null = null
const _listeners: Array<(tz: string) => void> = []
let _fetching = false

function notifyListeners(tz: string) {
  _listeners.forEach((fn) => fn(tz))
}

function fetchOnce() {
  if (_cached || _fetching) return
  _fetching = true
  apiClient
    .get('/profile')
    .then((r) => {
      const payload = r.data?.data ?? r.data
      const tz = (payload?.timezone as string) || 'UTC'
      _cached = tz
      notifyListeners(tz)
    })
    .catch(() => {
      _cached = 'UTC'
      notifyListeners('UTC')
    })
    .finally(() => {
      _fetching = false
    })
}

/**
 * Clears the cached profile timezone so the next call to useProfileTimezone()
 * re-fetches from the API. Call this after the user saves a new timezone in
 * their settings.
 */
export function clearProfileTimezoneCache() {
  _cached = null
}

/**
 * Returns the current user's own profile timezone (IANA string, e.g. "Asia/Kolkata").
 * All client components share a single fetch — subsequent calls return the cached value.
 * Falls back to "UTC" if the profile has no timezone set.
 */
export function useProfileTimezone(): string {
  const [tz, setTz] = useState<string>(_cached ?? 'UTC')

  useEffect(() => {
    if (_cached) {
      setTz(_cached)
      return
    }
    const handler = (val: string) => setTz(val)
    _listeners.push(handler)
    fetchOnce()
    return () => {
      const idx = _listeners.indexOf(handler)
      if (idx !== -1) _listeners.splice(idx, 1)
    }
  }, [])

  return tz
}
